import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import {
  AlertCircle,
  AlertTriangle,
  Ban,
  CheckCircle2,
  Inbox,
  Mail,
  PauseCircle,
  Plug,
  RefreshCw,
  Settings as SettingsIcon,
  ShieldCheck,
  Unplug,
} from "lucide-react";

import MailboxService, { MailboxConnection } from "../../_services/MailboxService";
import "./mailbox.css";

type Provider = "gmail" | "outlook";
type StatusKey = "active" | "paused" | "error" | "revoked";

const STATUS_META: Record<StatusKey, { tone: "success" | "warn" | "danger" | "muted"; label: string; Icon: typeof CheckCircle2 }> = {
  active: { tone: "success", label: "Active", Icon: CheckCircle2 },
  paused: { tone: "warn", label: "Paused", Icon: PauseCircle },
  error: { tone: "danger", label: "Error", Icon: AlertTriangle },
  revoked: { tone: "muted", label: "Revoked", Icon: Ban },
};

const providerLabel = (p: string) => (p === "gmail" ? "Gmail" : p === "outlook" ? "Outlook" : p);

const MailboxConnectionsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const [connections, setConnections] = useState<MailboxConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<MailboxConnection | null>(null);
  const [info, setInfo] = useState<{ kind: "success" | "error"; message: string } | null>(null);

  // Pick up ?result=connected&detail=email or ?result=error&detail=... from the
  // OAuth callback redirect so we can flash a message.
  const callbackBanner = useMemo(() => {
    const qs = new URLSearchParams(location.search);
    const result = qs.get("result");
    const detail = qs.get("detail");
    if (!result) return null;
    if (result === "connected") {
      return { kind: "success" as const, message: `Connected ${detail ? decodeURIComponent(detail) : "mailbox"}.` };
    }
    return { kind: "error" as const, message: `Could not connect: ${detail ? decodeURIComponent(detail) : "unknown error"}` };
  }, [location.search]);

  useEffect(() => {
    if (callbackBanner) setInfo(callbackBanner);
  }, [callbackBanner]);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    MailboxService.list()
      .then((res) => setConnections((res.data && res.data.connections) || []))
      .catch((err) => setError(err?.response?.data?.error?.message || err?.message || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const handleConnect = async (provider: Provider) => {
    setError(null);
    try {
      const res =
        provider === "gmail"
          ? await MailboxService.initGoogle()
          : await MailboxService.initOutlook();
      const url = res.data && res.data.redirectUrl;
      if (!url) throw new Error("No consent URL returned");
      window.location.href = url;
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Failed to start OAuth");
    }
  };

  const handleDisconnect = async () => {
    if (!confirmDelete) return;
    setBusyId(confirmDelete.id);
    try {
      await MailboxService.disconnect(confirmDelete.id);
      setInfo({ kind: "success", message: `Disconnected ${confirmDelete.accountEmail}.` });
      setConfirmDelete(null);
      load();
    } catch (err: any) {
      setInfo({
        kind: "error",
        message: err?.response?.data?.error?.message || err?.message || "Disconnect failed",
      });
    } finally {
      setBusyId(null);
    }
  };

  const handleSyncNow = async (conn: MailboxConnection) => {
    setBusyId(conn.id);
    try {
      const res = await MailboxService.triggerSync(conn.id);
      const stats: any = res.data && res.data.stats;
      setInfo({
        kind: "success",
        message: stats
          ? `Synced ${conn.accountEmail}: ${stats.matched ?? 0} matched, ${stats.insertedOutbound ?? 0} sent / ${stats.insertedInbound ?? 0} received added.`
          : `Sync triggered for ${conn.accountEmail}`,
      });
      load();
    } catch (err: any) {
      setInfo({
        kind: "error",
        message: err?.response?.data?.error?.message || err?.message || "Sync failed",
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mbx-page">
      <aside className="mbx-side">
        <h2>Mailbox</h2>
        <button type="button" className="mbx-nav-btn active">
          <Inbox size={16} className="mbx-nav-icon" />
          <span className="mbx-nav-label">My Connections</span>
          <span className="mbx-nav-pill">{connections.length}</span>
        </button>
        <button
          type="button"
          className="mbx-nav-btn"
          onClick={() => history.push("/settings/mailbox-admin")}
        >
          <SettingsIcon size={16} className="mbx-nav-icon" />
          <span className="mbx-nav-label">OAuth Settings</span>
        </button>

        <div className="mbx-divider" />
        <button
          type="button"
          className="mbx-nav-btn"
          onClick={() => history.push("/tenant-preferences")}
          style={{ fontSize: 13, color: "var(--text-muted)" }}
          title="Back to tenant preferences hub"
        >
          <ShieldCheck size={14} className="mbx-nav-icon" />
          <span className="mbx-nav-label">Tenant Preferences</span>
        </button>
      </aside>

      <main className="mbx-main">
        <div className="mbx-header">
          <div>
            <h1>Mailbox Connections</h1>
            <p className="mbx-subtitle">
              Connect your Gmail or Outlook mailbox so mail you send or receive from companies and
              students appears automatically in their conversation timeline.
            </p>
          </div>
        </div>

        {info && (
          <div className={`mbx-alert ${info.kind === "success" ? "mbx-alert-success" : "mbx-alert-error"}`}>
            {info.kind === "success" ? (
              <CheckCircle2 size={16} className="mbx-alert-icon" />
            ) : (
              <AlertCircle size={16} className="mbx-alert-icon" />
            )}
            <div className="mbx-alert-body">{info.message}</div>
            <button type="button" className="mbx-alert-close" onClick={() => setInfo(null)}>&times;</button>
          </div>
        )}
        {error && (
          <div className="mbx-alert mbx-alert-error">
            <AlertCircle size={16} className="mbx-alert-icon" />
            <div className="mbx-alert-body">{error}</div>
            <button type="button" className="mbx-alert-close" onClick={() => setError(null)}>&times;</button>
          </div>
        )}

        {/* Provider tiles */}
        <div className="mbx-card">
          <div className="mbx-card-head">
            <span className="mbx-card-icon"><Plug size={18} /></span>
            <h3>Connect a Mailbox</h3>
          </div>
          <p className="mbx-card-desc">
            Pick your provider. You'll be redirected to grant access, then bounced back here.
          </p>
          <div className="mbx-provider-grid">
            <div className="mbx-provider-tile">
              <div className="mbx-provider-icon gmail"><Mail size={20} /></div>
              <div className="mbx-provider-info">
                <div className="mbx-provider-name">Gmail</div>
                <div className="mbx-provider-sub">Google Workspace &amp; personal Gmail</div>
              </div>
              <button
                type="button"
                className="mbx-btn mbx-btn-primary mbx-btn-sm"
                onClick={() => handleConnect("gmail")}
              >
                Connect
              </button>
            </div>
            <div className="mbx-provider-tile">
              <div className="mbx-provider-icon outlook"><Mail size={20} /></div>
              <div className="mbx-provider-info">
                <div className="mbx-provider-name">Outlook</div>
                <div className="mbx-provider-sub">Microsoft 365 &amp; Outlook.com</div>
              </div>
              <button
                type="button"
                className="mbx-btn mbx-btn-primary mbx-btn-sm"
                onClick={() => handleConnect("outlook")}
              >
                Connect
              </button>
            </div>
          </div>
        </div>

        {/* Connections list */}
        <div className="mbx-card">
          <div className="mbx-card-head">
            <span className="mbx-card-icon"><Inbox size={18} /></span>
            <h3>
              Connected Mailboxes{" "}
              <span style={{ color: "var(--text-muted)", fontWeight: 500 }}>({connections.length})</span>
            </h3>
          </div>

          {loading ? (
            <div className="mbx-loading"><div className="mbx-spinner" /> Loading connections…</div>
          ) : connections.length === 0 ? (
            <div className="mbx-empty">
              <div className="mbx-empty-icon"><Inbox size={26} /></div>
              <h3>No mailboxes connected yet</h3>
              <p>Use the tiles above to connect a Gmail or Outlook account.</p>
            </div>
          ) : (
            <div className="mbx-conn-list">
              {connections.map((c) => {
                const meta = STATUS_META[(c.status as StatusKey)] || STATUS_META.error;
                const StatusIcon = meta.Icon;
                const provClass = c.provider === "gmail" ? "gmail" : "outlook";
                return (
                  <div key={c.id} className="mbx-conn-row">
                    <div className={`mbx-conn-avatar ${provClass}`}>
                      <Mail size={18} />
                    </div>
                    <div className="mbx-conn-body">
                      <div className="mbx-conn-email">
                        {c.accountEmail}
                        <span className="mbx-provider-chip">{providerLabel(c.provider)}</span>
                      </div>
                      <div className="mbx-conn-meta">
                        {c.lastSyncedAt
                          ? `Last synced ${new Date(c.lastSyncedAt).toLocaleString()}`
                          : "Not synced yet"}
                        {c.lastError ? (
                          <span className="mbx-error-text">{c.lastError}</span>
                        ) : null}
                      </div>
                    </div>
                    <span className={`mbx-pill ${meta.tone}`}>
                      <StatusIcon size={11} /> {meta.label}
                    </span>
                    <div className="mbx-conn-actions">
                      <button
                        type="button"
                        className="mbx-btn mbx-btn-secondary mbx-btn-sm"
                        disabled={busyId === c.id}
                        onClick={() => handleSyncNow(c)}
                      >
                        <RefreshCw size={13} /> {busyId === c.id ? "Working…" : "Sync"}
                      </button>
                      <button
                        type="button"
                        className="mbx-btn mbx-btn-danger mbx-btn-sm"
                        disabled={busyId === c.id}
                        onClick={() => setConfirmDelete(c)}
                      >
                        <Unplug size={13} /> Disconnect
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Modal
        show={!!confirmDelete}
        onHide={() => setConfirmDelete(null)}
        centered
        dialogClassName="mbx-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Disconnect mailbox</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {confirmDelete && (
            <>
              <p>
                Disconnect <strong>{confirmDelete.accountEmail}</strong>?
              </p>
              <p style={{ color: "var(--text-muted)" }}>
                We'll stop syncing this mailbox. Messages already imported into the timeline stay
                in place. You can reconnect at any time.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setConfirmDelete(null)} disabled={!!busyId}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDisconnect} disabled={!!busyId}>
            {busyId ? "Disconnecting…" : "Disconnect"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MailboxConnectionsPage;
